// Proximity voice chat: open-mic WebRTC MESH between players. The game server
// only relays the signaling JSON (`rtc` messages, see PROTOCOL.md) — audio is
// Opus peer-to-peer, so no voice bytes ever cross the server. Each remote
// voice is fed through a near-field HRTF PannerNode on the SHARED Sfx
// AudioContext, whose listener already follows the headset (Sfx.listener), so
// nearby players are loud and directional and far ones fade out.
// Handshake (both ends must agree, see PROTOCOL.md): a player enabling voice
// broadcasts `join`; a voice-enabled peer with a LOWER id offers immediately,
// one with a higher id replies `here` so the joiner (lower id) offers —
// exactly one offerer per pair, no glare.
import * as THREE from 'three';
import { Sfx } from './audio.js';

export class Voice {
  constructor(net) {
    this.net = net;
    this.stream = null;      // local mic stream (null until prime())
    this.peers = new Map();  // pid -> {pc, pending, audioEl, nodes, buf, ring, level}
    this.active = false;     // joined the voice mesh
    this.muted = false;
    this.tun = {};           // tunables.voice
    net.onRtc = (m) => this._onSignal(m);
    net.onRemoteGone = (id) => this._close(id);
  }

  get available() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia
      && window.RTCPeerConnection);
  }

  get ready() { return !!this.stream; }

  // MUST run on a user gesture BEFORE entering the XR session: Quest shows the
  // mic permission prompt as a browser overlay that cannot be answered from
  // inside an immersive session.
  async prime() {
    if (this.stream) return;
    const s = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    this.stream = s;
    const tr = s.getAudioTracks()[0];
    if (tr) tr.onended = () => { this.dispose(); this.stream = null; };
    if (this.muted) this.setMuted(true);
  }

  start() {
    if (!this.stream || this.active) return;
    this.active = true;
    this.net.sendRtc({ kind: 'join' });
  }

  setMuted(m) {
    this.muted = !!m;
    if (this.stream) {
      for (const tr of this.stream.getAudioTracks()) tr.enabled = !this.muted;
    }
  }

  applyTunables(t) {
    this.tun = (t && t.voice) || {};
    for (const peer of this.peers.values()) this._applyPanner(peer);
  }

  // per-frame (after net.updateRemotes): move each voice to its player's head
  // and drive the speaking ring from the analyser level
  update(remotes) {
    if (!this.peers.size) return;
    const thresh = this.tun.speak_threshold ?? 0.02;
    for (const [pid, peer] of this.peers) {
      if (!peer.nodes) continue;
      const r = remotes.get(pid);
      let x, y, z;
      if (r && r.ent && r.ent.head && r.ent.head.p) {
        [x, y, z] = r.ent.head.p;               // reported headset world pos
      } else if (r && r.group) {
        x = r.group.position.x; y = r.group.position.y + 1.62; z = r.group.position.z;
      } else continue;                           // no entity yet: leave as-is
      const p = peer.nodes.panner;
      if (p.positionX) { p.positionX.value = x; p.positionY.value = y; p.positionZ.value = z; }
      else p.setPosition(x, y, z);

      peer.nodes.analyser.getByteTimeDomainData(peer.buf);
      let sum = 0;
      for (let i = 0; i < peer.buf.length; i++) {
        const v = (peer.buf[i] - 128) / 128;
        sum += v * v;
      }
      peer.level = Math.max(Math.sqrt(sum / peer.buf.length), peer.level * 0.92);
      if (r && r.parts && r.parts.head) {
        if (!peer.ring) peer.ring = makeRing();
        if (peer.ring.parent !== r.parts.head) r.parts.head.add(peer.ring);
        peer.ring.visible = peer.level > thresh;
        if (peer.ring.visible) {
          peer.ring.scale.setScalar(1 + Math.min(0.35, peer.level * 2));  // pulse with loudness
        }
      }
    }
  }

  dispose() {
    for (const pid of [...this.peers.keys()]) this._close(pid, true);
    this.active = false;
  }

  // ---------------------------------------------------------------- signaling
  async _onSignal(m) {
    if (!this.active || !this.stream) return;    // not in the mesh: ignore
    const pid = m.from;
    if (typeof pid !== 'number' || pid >= 1000) return;   // bots never speak
    try {
      switch (m.kind) {
        case 'join':                             // (re)joined: drop stale state
          this._close(pid);
          if (this.net.id < pid) await this._offer(pid);
          else this.net.sendRtc({ kind: 'here', to: pid });
          break;
        case 'here':                             // higher-id peer answered our join
          if (this.net.id < pid && !this.peers.has(pid)) await this._offer(pid);
          break;
        case 'offer': {
          const peer = this._peer(pid);
          await peer.pc.setRemoteDescription(m.data);
          await this._drainIce(peer);
          await peer.pc.setLocalDescription(await peer.pc.createAnswer());
          this.net.sendRtc({ kind: 'answer', to: pid, data: peer.pc.localDescription.toJSON() });
          break;
        }
        case 'answer': {
          const peer = this.peers.get(pid);
          if (peer) {
            await peer.pc.setRemoteDescription(m.data);
            await this._drainIce(peer);
          }
          break;
        }
        case 'ice': {
          const peer = this.peers.get(pid);
          if (!peer || !m.data) return;
          if (peer.pc.remoteDescription) await peer.pc.addIceCandidate(m.data);
          else peer.pending.push(m.data);        // ICE raced ahead of the offer
          break;
        }
        case 'bye': this._close(pid); break;
        default: break;
      }
    } catch (e) {
      console.warn('[voice] signaling failed:', m.kind, e);
    }
  }

  async _offer(pid) {
    if (this.peers.has(pid)) return;
    const peer = this._peer(pid);
    await peer.pc.setLocalDescription(await peer.pc.createOffer());
    this.net.sendRtc({ kind: 'offer', to: pid, data: peer.pc.localDescription.toJSON() });
  }

  _peer(pid) {
    let peer = this.peers.get(pid);
    if (peer) return peer;
    // ICE servers come from the server's welcome (dashboard setting → fork
    // env → gs.js). On a LAN, host candidates still connect directly and win;
    // the STUN/TURN entries only matter for peers behind different NATs
    // (internet play). An empty list restores pure-LAN behaviour.
    const pc = new RTCPeerConnection({ iceServers: this.net.ice || [] });
    peer = { pc, pending: [], audioEl: null, nodes: null, buf: null, ring: null, level: 0 };
    this.peers.set(pid, peer);
    for (const tr of this.stream.getAudioTracks()) pc.addTrack(tr, this.stream);
    pc.onicecandidate = (e) => {
      if (e.candidate) this.net.sendRtc({ kind: 'ice', to: pid, data: e.candidate.toJSON() });
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') this._close(pid);
    };
    pc.ontrack = (e) => {
      this._attach(peer, e.streams[0] || new MediaStream([e.track]));
    };
    return peer;
  }

  async _drainIce(peer) {
    while (peer.pending.length) {
      try { await peer.pc.addIceCandidate(peer.pending.shift()); }
      catch (e) { console.warn('[voice] ice failed:', e); }
    }
  }

  _attach(peer, stream) {
    if (peer.nodes) return;
    // Chromium quirk: WebRTC audio only flows once the stream is attached to a
    // media element — muted, so the panned graph below is the only audible path
    const el = document.createElement('audio');
    el.srcObject = stream;
    el.muted = true;
    el.play().catch(() => {});
    const c = Sfx.ctx();
    const src = c.createMediaStreamSource(stream);
    const analyser = c.createAnalyser();
    analyser.fftSize = 256;
    const gain = c.createGain();
    const panner = c.createPanner();
    panner.panningModel = Sfx.panModel();   // HRTF desktop, equalpower on Quest
    panner.distanceModel = 'inverse';
    src.connect(analyser);
    src.connect(gain);
    gain.connect(panner);
    panner.connect(c.destination);
    peer.audioEl = el;
    peer.nodes = { src, gain, panner, analyser };
    peer.buf = new Uint8Array(analyser.fftSize);
    this._applyPanner(peer);
  }

  _applyPanner(peer) {
    if (!peer.nodes) return;
    const t = this.tun;
    peer.nodes.panner.refDistance = t.ref_distance_m ?? 2;
    peer.nodes.panner.rolloffFactor = t.rolloff ?? 1.2;
    peer.nodes.panner.maxDistance = t.max_distance_m ?? 60;
    peer.nodes.gain.gain.value = t.gain ?? 1;
  }

  _close(pid, notify) {
    const peer = this.peers.get(pid);
    if (!peer) return;
    this.peers.delete(pid);
    if (notify && this.net.connected) this.net.sendRtc({ kind: 'bye', to: pid });
    try { peer.pc.close(); } catch { /* already closed */ }
    if (peer.ring && peer.ring.parent) peer.ring.parent.remove(peer.ring);
    if (peer.nodes) {
      try {
        peer.nodes.src.disconnect();
        peer.nodes.gain.disconnect();
        peer.nodes.panner.disconnect();
      } catch { /* context torn down */ }
    }
    if (peer.audioEl) peer.audioEl.srcObject = null;
  }
}

// speaking indicator: a small halo just above the head, child of the avatar's
// head mesh (net.js makeAvatar), so it rides the gaze like the face features
function makeRing() {
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(0.13, 0.014, 6, 20),
    new THREE.MeshBasicMaterial({ color: 0x7dd3a0 }),
  );
  ring.rotation.x = Math.PI / 2;
  ring.position.y = 0.28;
  return ring;
}
